import styled, { keyframes } from 'styled-components';

export const CartPage = styled.div`
	text-align: left;
`;

const rise = keyframes`
	to { transform: none; }
`;

export const CtHead = styled.div`
	padding: 150px var(--pad) 30px;
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
	flex-wrap: wrap;

	h1 {
		font-size: clamp(44px, 6.4vw, 92px);
		line-height: 0.94;
	}

	/* homepage-hero row treatment: the title rises out of an overflow clip;
	   the padding/margin pair gives diacritics (Ć, Č) headroom above the
	   0.94 line box so the clip doesn't shave their accents off */
	h1 .row {
		display: block;
		overflow: hidden;
		padding-top: 0.14em;
		margin-top: -0.14em;
	}

	h1 .row span {
		display: inline-block;
		transform: translateY(110%);
		animation: ${rise} 1s cubic-bezier(0.2, 0.65, 0.2, 1) forwards;
	}

	/* founder pick 2026-08-17: the hand note reads AFTER the title, not
	   above it (desktop matched to mobile) */
	> div {
		display: flex;
		flex-direction: column;
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(25px, 2.4vw, 33px);
		display: block;
		order: 2;
		margin-top: 8px;
		transform: rotate(-1.6deg);
		animation: ct-fade 0.7s ease 0.35s backwards;
	}

	.cnt {
		color: var(--mut);
		font-size: 15.5px;
		font-weight: 600;
		animation: ct-fade 0.7s ease 0.45s backwards;
	}

	@keyframes ct-fade {
		from {
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		h1 .row span {
			transform: none;
		}

		h1 .row span,
		.hand,
		.cnt {
			animation: none;
		}
	}
`;

export const CtList = styled.div`
	border-top: 1px solid var(--hair);
	margin-top: 26px;
`;

export const CtItem = styled.div`
	border-bottom: 1px solid var(--hair);
	padding: 26px var(--pad);

	.row {
		display: flex;
		align-items: baseline;
		gap: clamp(14px, 3vw, 34px);
	}

	.ttl {
		flex: 1;
	}

	/* the app has no global heading-margin reset, so without this the UA's
	   1em margins push the title out of line with the delete/price column */
	h3 {
		margin: 0;
		font-size: clamp(20px, 2.2vw, 30px);
	}

	/* a gift line: paid here, delivered as a code by mail */
	.gift-tag {
		display: block;
		margin-top: 6px;
		font-size: 13.5px;
		color: var(--mut);
	}

	/* premade collab lockup: Q logo ✕ author logo/handwritten name */
	.collab {
		display: flex;
		align-items: center;
		gap: 9px;
		margin-top: 6px;
		width: fit-content;
	}

	.collab .author-img {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
	}

	.collab .author-img img {
		display: block;
		height: 20px;
		width: auto;
	}

	.collab .author-img.sq img {
		height: 24px;
	}

	.collab .author-img .v-neon {
		display: none;
	}

	.collab .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 22px;
		line-height: 1;
		color: var(--teal-dark);
		white-space: nowrap;
	}

	.meta {
		font-size: 15px;
		color: var(--mut);
		font-weight: 600;
		white-space: nowrap;
	}

	.price {
		font-family: var(--fd);
		font-size: clamp(19px, 1.9vw, 26px);
		color: var(--coral);
		white-space: nowrap;
	}

	.rm {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 15.5px;
		font-weight: 600;
		font-family: var(--fb);
		color: var(--mut);
		text-decoration: none;
		cursor: pointer;
		transition: color 0.2s;
		white-space: nowrap;
		background: none;
		border: none;
	}

	/* optical alignment: the ✕ centers on the full line box, which sits a
	   hair above the lowercase body of "obriši" */
	.rm svg {
		transform: translateY(1px);
	}

	.rm:hover {
		color: var(--coral);
	}

	/* desktop never shows the below-details strip */
	.below {
		display: none;
	}

	/* mobile (founder picks 2026-07-23, revised 2026-08-17): A1 sizes; the
	   delete ✕ gets a dedicated right column and always sits ABOVE
	   the price, so the title wraps in the remaining width instead of
	   sharing a line - except generator items, where the pair reads after
	   the breakdown (.gen versions hide, .below strip shows) */
	@media (max-width: 720px) {
		.row {
			display: grid;
			grid-template-columns: 1fr auto;
			align-items: start;
			gap: 6px 14px;
		}

		.ttl {
			grid-column: 1;
			grid-row: 1 / 3;
		}

		h3 {
			font-size: 17.5px;
		}

		.collab {
			gap: 7px;
		}

		.collab svg {
			width: 15px;
			height: 15px;
		}

		.collab svg + svg {
			width: 9px;
			height: 9px;
		}

		.collab .author-img img {
			height: 17px;
		}

		.collab .author-img.sq img {
			height: 20px;
		}

		.collab .author-hand {
			font-size: 19px;
		}

		.price {
			grid-column: 2;
			grid-row: 2;
			justify-self: end;
			font-size: 17px;
		}

		.rm.gen,
		.price.gen {
			display: none;
		}

		.below {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			gap: 8px;
			margin-top: 10px;
		}

		.rm {
			grid-column: 2;
			grid-row: 1;
			justify-self: end;
			/* center the 30px hit area on the title's first line
			   (17.5px h3 at 1.02 line-height) */
			margin-top: calc((17.5px * 1.02 - 30px) / 2);
			width: 30px;
			height: 30px;
			justify-content: center;
			gap: 0;
		}

		.below .rm {
			margin-top: 0;
		}

		.rm .rm-lbl {
			display: none;
		}

		.rm svg {
			width: 13px;
			height: 13px;
			/* no label on mobile - the ✕ centers in the button on its own */
			transform: none;
		}
	}
`;

/** Generator item breakdown (founder pick 2026-08-27, proposal 09·C): no
 *  columns and no header row — difficulty dots and the question count ride
 *  inline next to the name that owns them. Exactly two configurator modes:
 *  whole-category pick (a single row - dots + count on the category, the
 *  subcategory pool is not shown at all) or per-subcategory pick (dots +
 *  count on each picked subcategory, muted computed sum on the category). */
export const CtBreak = styled.div`
	margin: 14px 0 0;
	padding-left: 2px;

	.br {
		padding: 10px 0;
		font-size: 15.5px;
		color: var(--brown);
		border-bottom: 1px dashed rgba(var(--ink-rgb), 0.08);
	}

	.br:last-child {
		border-bottom: none;
	}

	/* per-subcategory pick: the category is a plain header above its subrows */
	.br.hd {
		padding-bottom: 4px;
		border-bottom: none;
	}

	.br.sr {
		padding: 6px 0 6px 18px;
	}

	.cat {
		font-family: var(--fd);
		font-size: 15px;
		color: var(--ink);
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	.sn {
		font-size: 14.5px;
	}

	/* difficulty dots (06·A točke): teal = easy (the correct/go color), gold =
	   medium (the hand-note accent), coral = hard (the price/alert color);
	   excluded levels stay visible dimmed so absence is read, not guessed -
	   and color is never the only channel, position and the group title
	   carry it too */
	.lvt {
		display: inline-flex;
		gap: 5px;
		align-items: center;
		vertical-align: middle;
		margin-left: 9px;
	}

	.lt {
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background: rgba(var(--ink-rgb), 0.14);
	}

	.lt.l1.on {
		background: var(--logo);
	}

	.lt.l2.on {
		background: var(--gold);
	}

	.lt.l3.on {
		background: var(--coral);
	}

	/* count (07·A): picked counts speak the same display voice as category
	   names, the price and UKUPNO; the muted »pitanja« unit makes the inline
	   number self-describing now that the column header is gone */
	.qn {
		font-family: var(--fd);
		font-size: 17px;
		color: var(--ink);
		margin-left: 10px;
	}

	/* computed sum of a per-subcategory pick - same Fjalla voice, grayed and
	   a step smaller so it never competes with the actual picked counts */
	.qn.tot {
		font-size: 15px;
		color: var(--mut);
	}

	.unit {
		font-size: 11.5px;
		color: var(--mut);
		margin-left: 4px;
	}

	/* mobile: the inline layout survives as-is - rows just tighten */
	@media (max-width: 720px) {
		margin: 8px 0 0;

		.br {
			font-size: 14.5px;
			padding: 8px 0;
		}

		.br.sr {
			padding: 5px 0 5px 14px;
		}

		.cat {
			font-size: 14px;
		}

		.sn {
			font-size: 14px;
		}

		.qn {
			font-size: 16px;
		}

		.qn.tot {
			font-size: 14px;
		}
	}
`;

export const CtTotal = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 20px;
	padding: 30px var(--pad);
	border-bottom: 1px solid var(--hair);

	.lbl {
		font-family: var(--fd);
		font-size: clamp(24px, 2.6vw, 36px);
		text-transform: uppercase;
		color: var(--ink);
	}

	.sum {
		font-family: var(--fd);
		font-size: clamp(30px, 3.4vw, 46px);
		color: var(--coral);
	}

	@media (max-width: 720px) {
		.lbl {
			font-size: 20px;
		}

		.sum {
			font-size: 26px;
		}
	}
`;

export const CtPay = styled.div`
	padding: 44px var(--pad) 130px;
	max-width: calc(820px + 2 * var(--pad));

	.pt {
		font-family: var(--fd);
		font-size: clamp(22px, 2.2vw, 30px);
		text-transform: uppercase;
		margin-bottom: 24px;
		color: var(--ink);
	}

	/* leads the payment block, sitting right above the consents; the inner
	   OptionsBox self-centers (margin auto) elsewhere - here it hugs left */
	.card-logos {
		margin: 0 0 26px;
	}

	.card-logos > div {
		margin: 0;
	}

	/* card-brand pills follow the theme: clean white plates on the cream
	   page, dimmed warm cream on dark so they don't glare - the <a> pill IS
	   the plate, imgs sit directly on it */
	.card-logos a {
		background-color: ${({ theme }) =>
			theme.mode === 'dark' ? '#e7e2d8' : '#ffffff'};
		border: 1px solid rgba(0, 0, 0, 0.06);
	}

	.card-logos img {
		${({ theme }) => (theme.mode === 'dark' ? '' : 'opacity: 0.85;')}
	}

	.safe {
		display: block;
		margin-top: 14px;
		font-size: 15.5px;
		color: var(--mut);
		font-weight: 500;
	}

	/* payment-method tiles (mirrors the live site's picker): placeholders for
	   the wallet pays, kartično = Corvus, Q pay only when qpay is enabled */
	.providers {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 14px;
		margin: 14px 0 30px;
		max-width: 680px;
	}

	/* same surface language as the alerts: borderless soft tints with the
	   fixed 27px radius; selection is a teal wash, not a ring */
	.provider {
		border: none;
		border-radius: 27px;
		padding: 18px 10px;
		background: rgba(var(--ink-rgb), 0.06);
		cursor: pointer;
		transition: 0.15s;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 10px;
		min-height: 96px;
		color: var(--ink);
	}

	.provider .pv-mark {
		font-family: var(--fb);
		font-size: 15px;
		font-weight: 700;
	}

	.provider .pv-lbl {
		font-family: var(--fb);
		font-size: 13.5px;
		font-weight: 600;
		color: var(--brown);
		text-align: center;
	}

	.provider.on {
		background: ${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(0, 255, 194, 0.14)'
				: 'rgba(0, 128, 97, 0.12)'};
	}

	.provider.on svg {
		color: var(--teal-dark);
	}

	.provider.on .pv-lbl {
		color: var(--teal-dark);
	}

	.provider:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	.provider.soon {
		cursor: default;
		opacity: 0.55;
	}

	.soon-chip {
		font-size: 10.5px;
		font-weight: 700;
		letter-spacing: 1.2px;
		text-transform: uppercase;
		color: var(--mut);
		background: rgba(var(--ink-rgb), 0.07);
		border: none;
		border-radius: var(--r-pill);
		padding: 3px 10px;
	}

	.pay-btn {
		font-size: 19px;
		padding: 18px 44px;
	}

	@media (max-width: 720px) {
		.providers {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 10px;
		}

		/* same look as the profile rows' »pokreni« */
		.pay-btn {
			width: 100%;
			font-size: 14px;
			padding: 11px 22px;
		}
	}

	/* mobile: consent (privole) text matches the breakdown subrow size */
	@media (max-width: 720px) {
		label p {
			font-size: 14.5px;
		}
	}
`;

// ------------------------------------------------------------------ //
// Order-finish pages (OrderFinish / OrderFinishPayment).
// ------------------------------------------------------------------ //

export const FinishPage = styled.div`
	text-align: left;
	min-height: 100vh;
`;

/** Centered celebratory block: hand note, oversized Fjalla title, CTA. */
export const Celebrate = styled.div`
	padding: 150px var(--pad) 140px;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(26px, 2.6vw, 38px);
		display: inline-block;
		transform: rotate(-1.5deg);
		margin-bottom: 8px;
	}

	h1 {
		font-size: clamp(44px, 6.4vw, 92px);
	}

	p {
		max-width: 620px;
		margin: 28px auto 0;
		color: var(--brown);
		font-size: 16.5px;
	}

	p a {
		color: var(--teal-dark);
		font-weight: 600;
	}

	.cta {
		margin-top: 40px;
	}
`;

export const FinishPaymentPage = styled.div`
	text-align: left;
	min-height: 100vh;
	padding-bottom: 120px;
`;

export const FinishHead = styled.div`
	padding: 150px var(--pad) 34px;

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(26px, 2.6vw, 38px);
		display: inline-block;
		transform: rotate(-1.5deg);
		margin-bottom: 8px;
	}

	h1 {
		font-size: clamp(44px, 6.4vw, 92px);
	}

	p {
		max-width: 620px;
		margin: 24px 0 0;
		color: var(--brown);
		font-size: 16.5px;
	}

	p a {
		color: var(--teal-dark);
		font-weight: 600;
	}
`;

export const FinishAlertWrap = styled.div`
	padding: 150px var(--pad) 60px;
`;
